import { useEffect, useMemo, useState } from "react";
import { Info, Lock, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ApiClient, CatalogEndpoint } from "../../../../../../../shared/index";
import type { B2bEffectiveEndpoint } from "@/lib/api";

interface AccessDraft {
  featureUrlIds: number[];
  flags: { featureId: number; enabled: boolean; customValue: string | null }[];
  countryIds: number[];
  newsCategoryIds: number[];
}

/**
 * The access matrix — what this client may call.
 *
 * Three things the legacy shuttle list could not do:
 * 1. **Group by product** and show each endpoint's description, instead of two
 *    unsearchable columns of raw URL strings.
 * 2. **Explain a prefix grant.** The runtimes match with `path.Contains(grant)`, so a
 *    legacy grant of `/api/stocks/` silently unlocks every `/api/stocks/*` route. Those
 *    rows are shown read-only with their expansion; new grants are exact endpoints.
 * 3. **Answer "what can this client actually call?"** directly, in the effective-access
 *    panel, rather than leaving an operator to work it out from the raw paths.
 */
export function AccessTab({
  client,
  effectiveAccess,
  catalog,
  features,
  draft,
  onChange,
}: {
  client: ApiClient;
  effectiveAccess: B2bEffectiveEndpoint[];
  catalog: CatalogEndpoint[];
  features: { featureId: number; name: string }[];
  draft: AccessDraft | null;
  onChange: (draft: AccessDraft) => void;
}) {
  const [search, setSearch] = useState("");

  const current: AccessDraft = useMemo(
    () => ({
      featureUrlIds: client.grants
        .filter((g) => g.featureUrlId !== null)
        .map((g) => g.featureUrlId!),
      flags: client.featureFlags.map((f) => ({
        featureId: f.featureId,
        enabled: f.enabled,
        customValue: f.customValue,
      })),
      countryIds: client.countries.filter((c) => c.isActive).map((c) => c.countryId),
      newsCategoryIds: client.newsCategories.filter((n) => n.isActive).map((n) => n.newsCategoryId),
    }),
    [client]
  );

  const value = draft ?? current;
  const [granted, setGranted] = useState<Set<number>>(new Set(value.featureUrlIds));

  useEffect(() => {
    setGranted(new Set((draft ?? current).featureUrlIds));
  }, [draft, current]);

  const prefixGrants = client.grants.filter((g) => g.isPrefix);

  const byProduct = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? catalog.filter(
          (e) =>
            e.featureUrl.toLowerCase().includes(term) ||
            (e.docTitle ?? "").toLowerCase().includes(term) ||
            (e.description ?? "").toLowerCase().includes(term)
        )
      : catalog;

    const groups = new Map<string, CatalogEndpoint[]>();
    for (const endpoint of filtered) {
      const names = endpoint.products.length > 0 ? endpoint.products : ["Ungrouped"];
      for (const name of names) {
        const list = groups.get(name) ?? [];
        list.push(endpoint);
        groups.set(name, list);
      }
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [catalog, search]);

  function apply(next: Set<number>) {
    setGranted(next);
    onChange({ ...value, featureUrlIds: [...next] });
  }

  function toggle(featureUrlId: number) {
    const next = new Set(granted);
    if (next.has(featureUrlId)) next.delete(featureUrlId);
    else next.add(featureUrlId);
    apply(next);
  }

  function toggleProduct(endpoints: CatalogEndpoint[], select: boolean) {
    const next = new Set(granted);
    for (const endpoint of endpoints) {
      if (select) next.add(endpoint.featureUrlId);
      else next.delete(endpoint.featureUrlId);
    }
    apply(next);
  }

  if (client.authType === "full") {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border bg-card p-6">
          <Lock className="mt-0.5 h-5 w-5 text-muted-foreground" />
          <div>
            <h2 className="font-semibold">This client has full access</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              A full client may call every endpoint, so individual grants are not consulted.
              Change the access type on the Overview tab to grant endpoints individually.
            </p>
          </div>
        </div>
        <FeatureFlagsPanel
          features={features}
          value={value}
          onChange={(flags) => onChange({ ...value, flags })}
        />
      </div>
    );
  }

  if (client.authType === "denied" || client.authType === "limited") {
    return (
      <div className="rounded-xl border bg-card p-6">
        <h2 className="font-semibold">
          This client&rsquo;s access type is &ldquo;{client.authType}&rdquo;
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The API rejects it on the endpoints that carry an authorization attribute, whatever
          grants exist. Switch it to single-feature on the Overview tab to grant endpoints.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {prefixGrants.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          <div className="flex items-center gap-2 font-medium">
            <Info className="h-4 w-4" />
            This client has {prefixGrants.length} legacy prefix grant
            {prefixGrants.length === 1 ? "" : "s"}
          </div>
          <p className="mt-1">
            The API matches a grant as a substring of the route, so these unlock every endpoint
            beneath them. They are shown read-only; new grants are always exact endpoints.
          </p>
          <ul className="mt-2 space-y-1">
            {prefixGrants.map((grant) => (
              <li key={grant.id} className="flex items-center gap-2">
                <code className="rounded bg-amber-100 px-1.5 py-0.5 text-xs dark:bg-amber-900/50">
                  {grant.featureUrl}
                </code>
                <span className="text-xs">
                  unlocks{" "}
                  {effectiveAccess.filter((e) => e.via === grant.featureUrl).length} endpoint(s)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <section className="rounded-xl border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
            <div>
              <h2 className="font-semibold">Endpoints</h2>
              <p className="text-sm text-muted-foreground">
                {granted.size} of {catalog.length} granted
              </p>
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Filter endpoints…"
                className="w-64 pl-8"
              />
            </div>
          </div>

          <div className="max-h-[32rem] overflow-auto p-2">
            {byProduct.length === 0 && (
              <p className="p-4 text-sm text-muted-foreground">No endpoints match.</p>
            )}
            {byProduct.map(([productName, endpoints]) => {
              const allSelected = endpoints.every((e) => granted.has(e.featureUrlId));
              return (
                <div key={productName} className="mb-2">
                  <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-1.5">
                    <span className="text-sm font-medium">{productName}</span>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => toggleProduct(endpoints, !allSelected)}
                    >
                      {allSelected ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <ul>
                    {endpoints.map((endpoint) => (
                      <li key={`${productName}-${endpoint.featureUrlId}`}>
                        <label className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 hover:bg-muted/40">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={granted.has(endpoint.featureUrlId)}
                            onChange={() => toggle(endpoint.featureUrlId)}
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">
                              {endpoint.docTitle ?? endpoint.featureUrl}
                            </span>
                            {/* The path repeats only when a docs title stands in front of
                                it; without docs the title IS the path. */}
                            {endpoint.docTitle && (
                              <code className="block truncate text-xs text-muted-foreground">
                                {endpoint.featureUrl}
                              </code>
                            )}
                            {endpoint.description && (
                              <span className="block text-xs text-muted-foreground">
                                {endpoint.description}
                              </span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        <section className="space-y-6">
          <div className="rounded-xl border bg-card">
            <div className="border-b px-6 py-4">
              <h2 className="font-semibold">Effective access</h2>
              <p className="text-sm text-muted-foreground">
                What this client can call right now, including prefix expansion
              </p>
            </div>
            <div className="max-h-64 overflow-auto p-4">
              {effectiveAccess.length === 0 ? (
                <p className="text-sm text-destructive">
                  Nothing. Every call returns 401 until an endpoint is granted.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {effectiveAccess.map((endpoint) => (
                    <li key={endpoint.featureUrlId} className="text-xs">
                      <code>{endpoint.featureUrl}</code>
                      {endpoint.via !== endpoint.featureUrl && (
                        <Badge variant="outline" className="ml-2">
                          via {endpoint.via}
                        </Badge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <FeatureFlagsPanel
            features={features}
            value={value}
            onChange={(flags) => onChange({ ...value, flags })}
          />
        </section>
      </div>
    </div>
  );
}

/**
 * Feature flags change what a response *contains*, not what the client may call. The
 * value-carrying ones (currently only the access-token lifetime) get a typed field
 * rather than the legacy free-text box.
 */
function FeatureFlagsPanel({
  features,
  value,
  onChange,
}: {
  features: { featureId: number; name: string }[];
  value: AccessDraft;
  onChange: (flags: AccessDraft["flags"]) => void;
}) {
  const byId = new Map(value.flags.map((f) => [f.featureId, f]));

  function set(featureId: number, patch: Partial<AccessDraft["flags"][number]>) {
    const existing = byId.get(featureId) ?? { featureId, enabled: false, customValue: null };
    const next = value.flags.filter((f) => f.featureId !== featureId);
    next.push({ ...existing, ...patch });
    onChange(next);
  }

  return (
    <div className="rounded-xl border bg-card">
      <div className="border-b px-6 py-4">
        <h2 className="font-semibold">Feature flags</h2>
        <p className="text-sm text-muted-foreground">
          These change response content, not which endpoints are reachable
        </p>
      </div>
      <ul className="divide-y">
        {features.map((feature) => {
          const flag = byId.get(feature.featureId);
          const isValueFlag = feature.name === "AccessTokenExpirationMinutes";
          return (
            <li key={feature.featureId} className="flex items-center justify-between gap-3 px-6 py-3">
              <label className="flex flex-1 cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={flag?.enabled ?? false}
                  onChange={(event) => set(feature.featureId, { enabled: event.target.checked })}
                />
                <span className="text-sm">{feature.name}</span>
              </label>
              {isValueFlag && flag?.enabled && (
                <div className="flex items-center gap-2">
                  <Label htmlFor={`flag-${feature.featureId}`} className="text-xs">
                    minutes
                  </Label>
                  <Input
                    id={`flag-${feature.featureId}`}
                    type="number"
                    className="h-8 w-20"
                    value={flag.customValue ?? ""}
                    placeholder="45"
                    onChange={(event) =>
                      set(feature.featureId, { customValue: event.target.value || null })
                    }
                  />
                </div>
              )}
            </li>
          );
        })}
        {features.length === 0 && (
          <li className="px-6 py-3 text-sm text-muted-foreground">No feature flags defined.</li>
        )}
      </ul>
    </div>
  );
}

export type { AccessDraft };
export { Button };
